import React, { useEffect, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import CloseIcon from '@mui/icons-material/Close'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { CopilotSession } from './hooks/useCopilotSession'
import { CopilotDraftHost } from './types'
import CopilotMessage from './CopilotMessage'
import CopilotComposer from './CopilotComposer'
import CopilotEmptyState from './CopilotEmptyState'

const PanelRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.custom2,
  color: theme.palette.text.primary,
}))

const PanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}))

const Transcript = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
}))

const NEAR_BOTTOM_PX = 80

interface CopilotPanelProps {
  host: CopilotDraftHost
  session: CopilotSession
  /** Renders a close button when provided (dock mode); omit in tab mode. */
  onClose?: () => void
}

/** Host-agnostic copilot dock: header, transcript, conflict banner, composer.
 * Host + session are owned by the responsive column so the conversation
 * survives breakpoint changes; all entity awareness flows through the host. */
const CopilotPanel: React.FC<CopilotPanelProps> = ({ host, session, onClose }) => {
  const transcriptRef = useRef<HTMLDivElement>(null)

  // Keep pinned to the bottom while streaming, but only if already near it
  useEffect(() => {
    const el = transcriptRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
    if (nearBottom) el.scrollTop = el.scrollHeight
  }, [session.messages, session.draftCards])

  return (
    <PanelRoot data-tour-id="copilot-panel">
      <PanelHeader>
        <SmartToyOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          AI Builder
        </Typography>
        {session.isStreaming && <CircularProgress size={14} />}
        <Tooltip title="New conversation">
          <span>
            <IconButton size="small" disabled={session.isStreaming} onClick={session.reset}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {onClose && (
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </PanelHeader>

      {host.pendingDraftSummary && (
        <Alert
          severity="info"
          sx={{ borderRadius: 0 }}
          action={
            <Box display="flex" gap={0.5}>
              <Button color="inherit" size="small" onClick={() => session.resolvePending('apply')}>
                Apply AI version
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => session.resolvePending('discard')}
              >
                Keep my edits
              </Button>
            </Box>
          }
        >
          You edited while the AI was building
        </Alert>
      )}

      {session.messages.length === 0 ? (
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {session.isResuming ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
              sx={{ height: '100%', p: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading previous session…
              </Typography>
            </Box>
          ) : (
            <>
              {session.error && (
                <Alert severity="error" sx={{ m: 1.5, py: 0 }}>
                  {session.error}
                </Alert>
              )}
              <CopilotEmptyState
                entityType={host.entityType}
                entityName={host.entityName}
                canResume={session.canResume}
                onResume={session.resume}
                onPrompt={session.send}
              />
            </>
          )}
        </Box>
      ) : (
        <Transcript ref={transcriptRef}>
          {session.messages.map((message, index) => (
            <CopilotMessage
              key={index}
              message={message}
              draftCards={session.draftCards}
              host={host}
            />
          ))}
          {session.error && (
            <Alert severity="error" sx={{ py: 0 }}>
              {session.error}
            </Alert>
          )}
        </Transcript>
      )}

      <CopilotComposer
        isStreaming={session.isStreaming}
        onSend={session.send}
        onStop={session.stop}
      />
    </PanelRoot>
  )
}

export default CopilotPanel
